const CANDIDATE = /[^\s,;<>()"'[\]]+/g;
const VALID = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export type EmailAdd = { fresh: string[]; leftover: string; problem: string | null };

/** Splits typed text into addresses not yet in `existing`, with a message for anything skipped. */
export const planEmailAdd = (
  text: string,
  existing: string[],
  owner: string | undefined,
): EmailAdd => {
  const { valid, invalid } = parseEmails(text);
  const fresh = valid.filter((email) => email !== owner && !existing.includes(email));
  const skippedOwner = owner !== undefined && valid.includes(owner);
  let problem: string | null = null;
  if (invalid.length > 0) {
    problem =
      invalid.length === 1
        ? `“${invalid[0]}” is not an email address.`
        : `${invalid.length} entries are not email addresses.`;
  } else if (valid.length === 0) {
    problem = "Enter an email address, such as alice@example.com.";
  } else if (fresh.length === 0) {
    problem = skippedOwner ? "You always have access." : "Already on the list.";
  }
  return { fresh, leftover: invalid.join(", "), problem };
};

export type ParsedEmails = { valid: string[]; invalid: string[] };

export const isEmail = (value: string): boolean => VALID.test(value) && value.length <= 254;

/** Extracts addresses from typed or pasted text such as `Alice <a@x.com>, b@y.com`. */
export const parseEmails = (text: string): ParsedEmails => {
  const tokens = text.match(CANDIDATE) ?? [];
  const valid = new Set<string>();
  const invalid: string[] = [];
  const withAt = tokens.filter((token) => token.includes("@"));
  const considered = withAt.length > 0 ? withAt : tokens;
  for (const token of considered) {
    const email = token.toLowerCase().replace(/^mailto:/, "");
    if (isEmail(email)) valid.add(email);
    else invalid.push(token);
  }
  return { valid: [...valid], invalid };
};
